import { faTruckBolt } from '@fortawesome/pro-light-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  Action,
  Alert,
  Card,
  ConfirmModal,
  Icon,
  Modal,
  NumberInput,
  RelativeDateTime,
  SimpleSelect,
  SwitchInput,
} from 'corso-admin-ui';
import {
  CountryCode,
  CrewMerchantUi,
  rmaProviderName,
  ShipmentMethod,
  shipmentMethodName,
} from 'corso-types';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import api from '~/api';
import Address from '~/components/Address';
import DescriptionList, { Description } from '~/components/DescriptionList';
import Paginator from '~/components/ui/Paginator';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import ClaimLineItem from '~/features/claimReview/components/ClaimLineItem';
import {
  CUSTOM_PARCEL,
  CUSTOM_PARCEL_NAME,
  findPredefinedParcelByDimensions,
  parcelSelectOptions,
  predefinedParcels,
} from '~/features/settings/automations/eventParams/predefinedParcels';
import { useIntegrationSettingsData } from '~/hooks/useIntegrationSettings';
import { useReturnLocations } from '~/hooks/useReturnLocations';
import { usePaginate } from '~/hooks/usePaginate';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { isErrorMessage } from '~/utils/errorResponse';
import { formatter } from 'corso-web-lib';
import ShipmentConsolidatedDropoff from './ShipmentConsolidatedDropoff';
import ShipmentLabel from './ShipmentLabel';
import ShipmentPackingSlip from './ShipmentPackingSlip';

type ClaimShipmentQuoteAndCreate =
  CrewMerchantUi.ClaimShipmentCreateAndBuyRequest;

type ClaimShipmentCreate = CrewMerchantUi.ClaimReturnShipmentCreate;
const claimShipmentCreate = CrewMerchantUi.claimReturnShipmentCreateSchema;
type ClaimShipmentQuote = CrewMerchantUi.ClaimReturnShipmentQuoteRequest;

type AddressWithInternalName = CrewMerchantUi.ReturnLocation['address'] & {
  internalName?: string;
};

function gramsToOunces(grams: number) {
  return grams / 28.3495;
}

// generic so a narrowed set of methods keeps its union through to SimpleSelect
const toMethodOptions = <T extends ShipmentMethod>(shipmentTypes: T[]) =>
  shipmentTypes.map((shipmentType) => ({
    label: shipmentMethodName[shipmentType],
    value: shipmentType,
  }));

type QuickShipMethod =
  | typeof ShipmentMethod.consolidatedDropoff
  | typeof ShipmentMethod.label;

function useClaimShipmentMethods() {
  const claim = useClaim();

  const { data: { happyReturnsConfig, easyPostConfig, vesylConfig } = {} } =
    useIntegrationSettingsData();

  return {
    canOfferHappyReturns:
      !!happyReturnsConfig?.isEnabled &&
      claim.shippingAddress.countryCode === CountryCode.US,
    hasLabelProvider: !!(easyPostConfig || vesylConfig),
  };
}

function ClaimQuickShip({
  toAddress,
  fromAddress,
  show,
  claimId,
  globalEOrderId,
  onClose,
}: {
  toAddress?: AddressWithInternalName;
  fromAddress: AddressWithInternalName;
  claimId: number;
  globalEOrderId?: string;
  show: boolean;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const toast = useToast();

  const claim = useClaim();

  const { canOfferHappyReturns, hasLabelProvider } = useClaimShipmentMethods();

  const mutationHandlers = {
    onError: (error: unknown) => {
      const message = isErrorMessage(error)
        ? error.message
        : 'Something went wrong. Please try again.';

      onClose();
      toast.show(message);
    },
    onSuccess: async () => {
      onClose();
      await claim.refetch();
    },
  };

  const { mutate: createAndBuyShipment, isPending } = useMutation({
    mutationFn: (body: ClaimShipmentQuoteAndCreate) =>
      api.store(storeId).claim(`${claimId}`).quoteAndCreateShipment(body),
    ...mutationHandlers,
  });

  const { mutate: createDropoffShipment, isPending: isDropoffPending } =
    useMutation({
      mutationFn: (body: ClaimShipmentCreate) =>
        api.store(storeId).claim(`${claimId}`).createShipment(body),
      ...mutationHandlers,
    });

  // a label needs somewhere to go, a drop-off consolidates at the Return Bar
  const availableMethods = [
    ...(canOfferHappyReturns ? [ShipmentMethod.consolidatedDropoff] : []),
    ...(hasLabelProvider && toAddress?.id ? [ShipmentMethod.label] : []),
  ] satisfies QuickShipMethod[];

  const defaultMethod = availableMethods[0] ?? null;

  const [method, setMethod] = useState<QuickShipMethod | null>(defaultMethod);

  // settings can resolve after mount, and each open should land back on the default
  useEffect(() => {
    if (show) setMethod(defaultMethod);
  }, [show, defaultMethod]);

  // Global-e decides the return destination, so there is no to/from address to pick
  if (globalEOrderId) {
    return (
      <ConfirmModal
        title="Quick Ship"
        prompt="A Global-e return label will be generated for this order, using the cheapest available return method."
        confirmText="Ship It"
        cancelText="Cancel"
        variant="primary"
        show={show}
        loading={isPending}
        onConfirm={() => {
          createAndBuyShipment({ kind: 'Global_E', globalEOrderId });
        }}
        onCancel={onClose}
      />
    );
  }

  // TODO: Look at the address types and make addressId required
  if (!method || !fromAddress.id) return null;

  const toAddressId = toAddress?.id;
  const fromAddressId = fromAddress.id;

  const isDropoff = method === ShipmentMethod.consolidatedDropoff;
  const isShipping = isPending || isDropoffPending;

  // a plain Modal rather than ConfirmModal, whose prompt is a <p> the select can't nest in
  return (
    <Modal
      title="Quick Ship"
      show={show || isShipping}
      onClose={onClose}
      actions={
        <>
          <Action onClick={onClose}>Cancel</Action>
          <Action
            variant="primary"
            loading={isShipping}
            onClick={() => {
              match(method)
                .with(ShipmentMethod.consolidatedDropoff, () => {
                  createDropoffShipment({
                    claimId,
                    returnShipmentType: ShipmentMethod.consolidatedDropoff,
                  });
                })
                .with(ShipmentMethod.label, () => {
                  if (!toAddressId) return;

                  createAndBuyShipment({
                    kind: 'EasyPost_Vesyl',
                    toAddressId,
                    fromAddressId,
                  });
                })
                .exhaustive();
            }}
          >
            Ship It
          </Action>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {availableMethods.length > 1 && (
          <SimpleSelect
            label="Return Method"
            options={toMethodOptions(availableMethods)}
            value={method}
            onChange={setMethod}
            details="The type of return shipment that will be created."
          />
        )}

        <p className="text-sm text-neutral-500">
          {isDropoff
            ? 'A QR code will be generated to drop off at any Return Bar location. No box or label needed.'
            : 'Return shipment will be generated using the default shipping provider, and the cheapest postage rate will be selected.'}
        </p>

        <div className="flex items-center justify-center gap-10 text-xs text-neutral-500">
          <div>
            <p className="text-sm font-bold">From:</p>
            <Address name={fromAddress.name} address={fromAddress} />
          </div>

          <Icon icon="arrowRight" className="h-6 w-6" />

          {isDropoff ? (
            <p className="text-sm font-bold">Happy Returns Return Bar®</p>
          ) : (
            toAddress && (
              <div>
                <p className="text-sm font-bold">To:</p>
                <Address name={toAddress.name} address={toAddress} />
              </div>
            )
          )}
        </div>
      </div>
    </Modal>
  );
}

function CreateClaimShipment({
  show,
  onClose,
  addresses,
}: {
  show: boolean;
  onClose: () => void;
  addresses: AddressWithInternalName[];
}) {
  const toast = useToast();
  const storeId = useStoreId();

  const claim = useClaim();

  const totalWeightInOz = gramsToOunces(
    claim.claimLineItems.reduce(
      (acc, claimLineItem) =>
        acc + claimLineItem.originalStoreOrderLineItem.grams,
      0,
    ),
  );

  const [currentStep, setCurrentStep] = useState(0);

  const [parcel, setParcel] = useState<{
    length: number;
    width: number;
    height: number;
  } | null>(null);

  const [isParcelEnabled, setIsParcelEnabled] = useState(false);

  const { canOfferHappyReturns } = useClaimShipmentMethods();

  const shipmentOptions = toMethodOptions([
    ShipmentMethod.label,
    ShipmentMethod.packingSlip,
    ...(canOfferHappyReturns ? [ShipmentMethod.consolidatedDropoff] : []),
  ]);

  const formId = 'create-claim-shipment-form';
  const {
    formState: { errors },
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
  } = useForm<ClaimShipmentCreate>({
    resolver: zodResolver(claimShipmentCreate),
    defaultValues: {
      claimId: claim.id,
      returnShipmentType: ShipmentMethod.packingSlip,
      // defaulting the from address to the claim's shipping address
      fromAddressId: claim.shippingAddress.id,
      // defaulting the to address to the first available address
      toAddressId: addresses[0]?.id,
    },
  });

  const resetParcel = () => {
    setIsParcelEnabled(false);
    setParcel(null);
  };

  const closeAndReset = () => {
    onClose();
    reset();
    setCurrentStep(0);
    resetParcel();
  };

  const goBack = () => {
    reset();
    setCurrentStep(0);
  };

  const { mutate: createReturnShipment, isPending } = useMutation({
    mutationFn: (body: ClaimShipmentCreate) =>
      api.store(storeId).claim(`${claim.id}`).createShipment(body),
    onSuccess: async () => {
      closeAndReset();
      await claim.refetch();
    },
    onError: (error) => {
      const message = isErrorMessage(error)
        ? error.message
        : 'Something went wrong. Please try again.';

      closeAndReset();
      toast.show(message);
    },
  });

  const totalOz = Math.round(totalWeightInOz);
  // packages of a pound or more read more naturally in lbs
  const preferLb = totalOz >= 16;

  const [packageWeight, setPackageWeight] = useState(
    preferLb ? Math.round((totalOz / 16) * 100) / 100 : totalOz,
  );
  const [weightUnit, setWeightUnit] = useState<'lb' | 'oz'>(
    preferLb ? 'lb' : 'oz',
  );

  const weightInOunces =
    (weightUnit === 'lb' ? packageWeight * 16 : packageWeight) || 0;

  const {
    mutateAsync: getShipmentQuotes,
    isPending: isRatesPending,
    data: quoteResp,
  } = useMutation({
    mutationFn: (body: ClaimShipmentQuote) =>
      // TODO verify what happens when a shipping provider is not available; i.e. has been removed/disconnected
      api.store(storeId).claim(`${claim.id}`).createShipmentQuote(body),
  });

  useEffect(() => {
    if (quoteResp?.kind === 'failure' || !quoteResp) return;

    setValue('shipmentIdFromPlatform', quoteResp.shipmentIdFromPlatform);
  }, [quoteResp, setValue]);

  const selectedToAddressId = watch('toAddressId');
  const selectedFromAddressId = watch('fromAddressId');
  const selectedType = watch('returnShipmentType');

  const isConsolidatedDropoff =
    selectedType === ShipmentMethod.consolidatedDropoff;

  const toAddress = addresses.find((loc) => loc.id === selectedToAddressId);
  const fromAddress = addresses.find((loc) => loc.id === selectedFromAddressId);

  const rates = quoteResp?.kind === 'success' ? quoteResp.rates : [];

  const rateErrors = quoteResp?.kind === 'failure' ? quoteResp.errors : [];

  const isSameAddress = selectedToAddressId === selectedFromAddressId;

  const isManualShipment = selectedFromAddressId !== claim.shippingAddress.id;

  const selectedParcelName = parcel
    ? (findPredefinedParcelByDimensions(parcel)?.name ?? CUSTOM_PARCEL_NAME)
    : CUSTOM_PARCEL_NAME;

  return (
    <Modal
      title="Create Shipment"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={currentStep === 0 ? closeAndReset : goBack}>
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Action>

          {/* // create a label */}
          {selectedType === ShipmentMethod.packingSlip && (
            <Action
              variant="primary"
              type="submit"
              form={formId}
              disabled={isPending || isSameAddress}
              loading={isPending}
            >
              Create
            </Action>
          )}

          {isConsolidatedDropoff && (
            <Action
              variant="primary"
              type="submit"
              form={formId}
              disabled={isPending}
              loading={isPending}
            >
              Create
            </Action>
          )}

          {selectedType === ShipmentMethod.label &&
            (currentStep === 0 ? (
              <Action
                variant="primary"
                type="submit"
                disabled={
                  isRatesPending || !selectedToAddressId || isSameAddress
                }
                loading={isRatesPending}
                onClick={() => {
                  getShipmentQuotes({
                    toAddressId: selectedToAddressId,
                    fromAddressId: selectedFromAddressId,
                    weightInOunces,
                    ...(isParcelEnabled && parcel
                      ? { parcel: { ...parcel } }
                      : {}),
                  })
                    .then(() => {
                      setCurrentStep(1);
                    })
                    .catch((e) => {
                      console.error(`Error getting shipment quotes:`, e);
                    });
                }}
              >
                Next
              </Action>
            ) : (
              <Action
                variant="primary"
                type="submit"
                form={formId}
                disabled={isPending || isSameAddress}
                loading={isPending}
              >
                Create
              </Action>
            ))}
        </>
      }
    >
      <form
        id={formId}
        // oxlint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit((submitPayload) => {
          // the resolver already narrowed to the matching member, addresses and all
          createReturnShipment(submitPayload);
        })}
        className="flex flex-col gap-4"
      >
        {currentStep !== 1 && !isConsolidatedDropoff && (
          <>
            <Controller
              name="fromAddressId"
              control={control}
              render={({ field: { onChange, value }, fieldState }) => (
                <SimpleSelect
                  label="From"
                  options={addresses.map((location) => ({
                    label: location.internalName
                      ? `${location.name} - ${location.internalName}`
                      : location.name,
                    value: `${location.id}`,
                  }))}
                  value={`${value}`}
                  onChange={(selected) =>
                    onChange(Number.parseInt(selected, 10))
                  }
                  details="The from address that will be used for this shipment."
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="toAddressId"
              control={control}
              render={({ field: { onChange, value }, fieldState }) => (
                <SimpleSelect
                  label="To"
                  options={addresses.map((location) => ({
                    label: location.internalName
                      ? `${location.name} - ${location.internalName}`
                      : location.name,
                    value: `${location.id}`,
                  }))}
                  value={`${value}`}
                  onChange={(selected) =>
                    onChange(Number.parseInt(selected, 10))
                  }
                  details="The to address that will be used for this shipment."
                  error={fieldState.error?.message}
                />
              )}
            />
          </>
        )}

        {isSameAddress && !isConsolidatedDropoff && (
          <Alert heading="Address Issue" variant="warning">
            The from and to addresses are the same.
          </Alert>
        )}

        {isManualShipment && !isSameAddress && !isConsolidatedDropoff && (
          <Alert heading="Manual Shipment" variant="info">
            This is a manual shipment, and will not trigger Automation
            workflows.
          </Alert>
        )}

        {isConsolidatedDropoff && (
          <Alert heading="Happy Returns" variant="info">
            A QR code will be generated to drop off at any Return Bar location.
            No box or label needed.
          </Alert>
        )}

        {toAddress && fromAddress && (
          <div className="flex items-center justify-center gap-10 text-xs text-neutral-500">
            <div>
              <p className="text-sm font-bold">From:</p>
              <Address name={fromAddress.name} address={fromAddress} />
            </div>

            <Icon icon="arrowRight" className="h-6 w-6" />

            {isConsolidatedDropoff ? (
              <p className="text-sm font-bold">Happy Returns Return Bar®</p>
            ) : (
              <div>
                <p className="text-sm font-bold">To:</p>
                <Address name={toAddress.name} address={toAddress} />
              </div>
            )}
          </div>
        )}

        {currentStep === 0 ? (
          <>
            <Controller
              name="returnShipmentType"
              control={control}
              render={({ field: { onChange, value } }) => (
                <SimpleSelect
                  label="Return Method"
                  options={shipmentOptions}
                  value={value}
                  onChange={onChange}
                  details="The type of return shipment that will be created."
                  error={errors.returnShipmentType?.message}
                />
              )}
            />
            {selectedType === ShipmentMethod.label && (
              <>
                <NumberInput
                  id="package-weight"
                  required
                  min={0}
                  step={0.01}
                  label="Weight"
                  details="The combined weight of the items in the shipment."
                  onChange={(e) => setPackageWeight(e.target.valueAsNumber)}
                  value={Number.isNaN(packageWeight) ? null : packageWeight}
                  addon={{
                    outsideEnd: (
                      <div className="md:w-24">
                        <SimpleSelect
                          label="Weight unit"
                          labelVisuallyHidden
                          options={[
                            { value: 'oz', label: 'oz' },
                            { value: 'lb', label: 'lb' },
                          ]}
                          value={weightUnit}
                          onChange={setWeightUnit}
                        />
                      </div>
                    ),
                  }}
                />
                <SwitchInput
                  id="enable-parcel"
                  label="Specify Parcel Dimensions"
                  details="Provide custom dimensions for the shipment if required by your shipping provider."
                  checked={isParcelEnabled}
                  onChange={(e) => {
                    setIsParcelEnabled(e.target.checked);
                    setParcel(
                      e.target.checked
                        ? {
                            length: CUSTOM_PARCEL.length,
                            width: CUSTOM_PARCEL.width,
                            height: CUSTOM_PARCEL.height,
                          }
                        : null,
                    );
                  }}
                />
                {isParcelEnabled && parcel && (
                  <>
                    <SimpleSelect
                      options={parcelSelectOptions}
                      label="Type"
                      value={selectedParcelName}
                      defaultValue={selectedParcelName}
                      onChange={(name) => {
                        const found = predefinedParcels.find(
                          (p) => p.name === name,
                        );
                        if (!found) return;
                        setParcel({
                          length: found.length,
                          width: found.width,
                          height: found.height,
                        });
                      }}
                    />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <NumberInput
                        id="parcel-length"
                        required
                        step={0.01}
                        label="Length"
                        addon={{ insideEnd: 'in' }}
                        value={parcel.length}
                        onChange={(e) =>
                          setParcel((prev) => ({
                            ...(prev ?? parcel),
                            length: e.target.valueAsNumber,
                          }))
                        }
                      />
                      <NumberInput
                        id="parcel-width"
                        required
                        step={0.01}
                        label="Width"
                        addon={{ insideEnd: 'in' }}
                        value={parcel.width}
                        onChange={(e) =>
                          setParcel((prev) => ({
                            ...(prev ?? parcel),
                            width: e.target.valueAsNumber,
                          }))
                        }
                      />
                      <NumberInput
                        id="parcel-height"
                        required
                        step={0.01}
                        label="Height"
                        addon={{ insideEnd: 'in' }}
                        value={parcel.height}
                        onChange={(e) =>
                          setParcel((prev) => ({
                            ...(prev ?? parcel),
                            height: e.target.valueAsNumber,
                          }))
                        }
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          !isRatesPending &&
          rates.length > 1 && (
            <Controller
              name="rate"
              control={control}
              render={({ field: { onChange, value }, fieldState }) => {
                const parsedValue =
                  claimShipmentCreate.options[1].shape.rate.safeParse(
                    // part of the issue here is that there's no value set, but type-wise it's expected to exist at this point
                    // TODO determine why `value` seems to be set as a `string`, widening type to `unknown` to enforce resolution, and generally resolve to remove this
                    value,
                  );

                return (
                  <SimpleSelect
                    label="Rate"
                    placeholder="Select a rate"
                    options={rates.map((rate) => ({
                      // TODO propagate the currency code from the rate and format it accordingly
                      label: (
                        <div>
                          {rate.service}{' '}
                          <div className="flex flex-row items-center gap-2">
                            <p className="text-xs text-neutral-500">
                              {rate.carrier} -
                            </p>
                            <p className="text-xs text-neutral-500">
                              {formatter.currency(rate.cost, rate.currency)}
                            </p>
                          </div>
                        </div>
                      ),
                      // label: `${rate.carrier} - ${rate.service} - ${formatter.currency(rate.cost, 'USD')}`,
                      value: rate.id,
                    }))}
                    value={parsedValue.data?.id}
                    onChange={(selected) =>
                      onChange(rates.find((rate) => rate.id === selected))
                    }
                    details="The rate that will be used for the shipment."
                    error={fieldState.error?.message}
                  />
                );
              }}
            />
          )
        )}
      </form>

      {!isRatesPending && rateErrors.length !== 0 && currentStep === 1 && (
        <div className="mt-4">
          <Alert heading="Failed to Create Shipment" variant="critical">
            <ul className="flex flex-col gap-2">
              {rateErrors.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Alert>
        </div>
      )}
    </Modal>
  );
}

function ClaimShipmentLineItemModal({
  show,
  shipment,
  onClose,
}: {
  shipment: CrewMerchantUi.ClaimShipment;
  onClose: () => void;
  show: boolean;
}) {
  const isLabel = shipment.returnShipmentType === ShipmentMethod.label;
  const dropoffCode =
    shipment.returnShipmentType === ShipmentMethod.consolidatedDropoff
      ? shipment.consolidatedDropoffCode
      : null;

  const carrier = dropoffCode
    ? 'Happy Returns'
    : ((isLabel ? shipment.carrierName : undefined) ?? null);
  const serviceName = dropoffCode
    ? 'Return Bar'
    : ((isLabel ? shipment.carrierServiceName : undefined) ?? null);

  const parcelName =
    shipment.returnShipmentType !== 'Consolidated_Dropoff'
      ? shipment.customInstructions?.parcelName
      : null;

  const descriptions: Description[] = [];

  if (dropoffCode) {
    descriptions.push({
      term: 'Confirmation Code',
      details: dropoffCode,
    });
  }
  if (parcelName) {
    descriptions.push({
      term: 'Parcel Name',
      details: parcelName,
    });
  }
  if (carrier) {
    descriptions.push({
      term: 'Carrier',
      details: carrier,
    });
  }
  if (serviceName) {
    descriptions.push({
      term: 'Service',
      details: serviceName,
    });
  }
  if (isLabel && shipment.tracker?.carrier.trackingCode) {
    descriptions.push({
      term: 'Tracking Number',
      details: shipment.tracker.carrier.trackingCode,
    });
  }

  return (
    <Modal onClose={onClose} show={show} title="Shipment Details">
      <DescriptionList descriptions={descriptions} />
      <Card.Line>Line Items</Card.Line>
      {shipment.claimLineItems.map((claimLineItem) => (
        <ClaimLineItem key={claimLineItem.id} claimLineItem={claimLineItem} />
      ))}
    </Modal>
  );
}

function ClaimShipmentDisplay({
  shipment,
}: {
  shipment?: CrewMerchantUi.ClaimShipment;
}) {
  const [showLineItems, setShowLineItems] = useState(false);
  if (!shipment) return null;

  const { createdOn, returnShipmentId, fromAddress, isReturnShipment } =
    shipment;

  const toAddress =
    shipment.returnShipmentType !== 'Consolidated_Dropoff'
      ? shipment.toAddress
      : null;

  const isLabelLike = shipment.returnShipmentType === ShipmentMethod.label;

  const rmaNumber = isLabelLike ? shipment.rmaNumber : undefined;
  const rmaProvider = isLabelLike ? shipment.rmaProvider : undefined;

  const hasRma = rmaNumber && rmaProvider;

  return (
    <>
      <ClaimShipmentLineItemModal
        shipment={shipment}
        show={showLineItems}
        onClose={() => setShowLineItems(false)}
      />
      <div className="flex flex-col justify-start gap-1">
        <Card.Line>
          <Card.Heading>#{returnShipmentId}</Card.Heading>
          <Action
            onClick={() => setShowLineItems(true)}
            icon="eye"
            variant="ghost"
            accessibilityLabel="View Line Items"
          />
        </Card.Line>
        <p className="text-xs text-neutral-500">
          {' '}
          {isReturnShipment ? 'Return Shipment' : 'Manual Shipment'}{' '}
        </p>
        <RelativeDateTime
          dateTime={createdOn}
          className="text-xs text-neutral-500"
        />
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-2">
        {!isReturnShipment && (
          <>
            <p className="text-xs text-neutral-500">From:</p>
            <Address address={fromAddress} name={fromAddress.name} />
          </>
        )}

        {toAddress && (
          <>
            <p className="text-xs text-neutral-500">To:</p>
            <Address address={toAddress} name={toAddress.name} />
          </>
        )}
      </div>

      {hasRma && (
        <div className="flex flex-col justify-start gap-1">
          <Card.Heading>RMA</Card.Heading>
          <p className="text-xs text-neutral-500">
            {rmaProviderName[rmaProvider]} #{rmaNumber}
          </p>
        </div>
      )}

      {match(shipment.returnShipmentType)
        .with(ShipmentMethod.label, () => <ShipmentLabel shipment={shipment} />)
        .with(ShipmentMethod.packingSlip, () => (
          <ShipmentPackingSlip shipment={shipment} />
        ))
        .with(ShipmentMethod.consolidatedDropoff, () => (
          <ShipmentConsolidatedDropoff shipment={shipment} />
        ))
        .exhaustive()}
    </>
  );
}

export default function ClaimShipmentsDisplay() {
  const claim = useClaim();

  const shipments = claim.claimReturnShipments;

  const [showCreateShipment, setShowCreateShipment] = useState(false);
  const closeCreateShipment = () => setShowCreateShipment(false);

  const { data: locations, isSuccess: areLocationsLoaded } =
    useReturnLocations(); // ? might need to display an error state

  const locationAddresses = locations?.map((loc) => ({
    internalName: loc.name,
    ...loc.address,
  }));

  const pagination = usePaginate({ items: shipments, perPage: 1 });
  const [shipment] = pagination.pageItems;

  const [showQuickShip, setShowQuickShip] = useState(false);

  const { canOfferHappyReturns, hasLabelProvider } = useClaimShipmentMethods();

  const quickShipLocation = locations?.find((loc) => loc.isQuickShipEnabled);

  const isGlobalE = !!claim.globalEOrderId;

  // Global-e claims can always quick ship (Global-e handles the label), and a Happy
  // Returns drop-off needs neither a provider nor a destination of its own
  const canQuickShip =
    areLocationsLoaded &&
    shipments.length === 0 &&
    (isGlobalE ||
      canOfferHappyReturns ||
      (!!quickShipLocation && hasLabelProvider));

  return (
    <Card>
      <Card.Line>
        <Card.Heading>Shipments</Card.Heading>
        {!isGlobalE && (
          <Action
            icon="add"
            variant="ghost"
            accessibilityLabel="Create Shipment"
            onClick={() => setShowCreateShipment(true)}
          />
        )}
      </Card.Line>

      {canQuickShip ? (
        <Action icon={faTruckBolt} onClick={() => setShowQuickShip(true)}>
          Quick Ship
        </Action>
      ) : (
        <ClaimShipmentDisplay shipment={shipment} />
      )}

      <Paginator pagination={pagination} />

      {areLocationsLoaded && (
        <>
          <ClaimQuickShip
            claimId={claim.id}
            show={showQuickShip}
            globalEOrderId={claim.globalEOrderId}
            toAddress={quickShipLocation?.address}
            fromAddress={{
              ...claim.shippingAddress,
              name: `${claim.shippingAddress.firstName} ${claim.shippingAddress.lastName}`,
            }}
            onClose={() => setShowQuickShip(false)}
          />
          <CreateClaimShipment
            show={showCreateShipment}
            onClose={closeCreateShipment}
            addresses={
              locationAddresses
                ? [
                    ...locationAddresses,
                    {
                      ...claim.shippingAddress,
                      // spreading the claim address last, so a location gets selected by default
                      name: `${claim.shippingAddress.firstName} ${claim.shippingAddress.lastName}`,
                    },
                  ]
                : []
            }
          />
        </>
      )}
    </Card>
  );
}
